import React, { useEffect, useState } from 'react';

import { PageHeader } from '../ui/PageHeader';
import { Card } from '../ui/Card';
import { createDonation, listMyDonations, Donation, DonationFrequency } from '../services/donationApi';

const GIVING_TYPES: { type: DonationFrequency; title: string; description: string }[] = [
  {
    type: 'one_time',
    title: 'One-time gift',
    description:
      'A single donation of any size, put to work immediately on active community and conservation projects. Ideal for travelers who want to give back after a trip.',
  },
  {
    type: 'monthly',
    title: 'Monthly supporter',
    description:
      'A recurring gift that gives our community partners predictable funding to plan ahead — school terms, ranger patrols, and habitat restoration all run on this kind of steady support.',
  },
  {
    type: 'sponsor_project',
    title: 'Sponsor a project',
    description:
      'Fund a specific initiative from our Impact Review — a scholarship cohort, a restoration site, a guide-training program — and receive direct updates on its progress.',
  },
  {
    type: 'corporate',
    title: 'Corporate & lodge partnerships',
    description:
      'For agencies, lodges, and brands that want a standing partnership: co-branded impact reporting, staff volunteering days, and a named seat in our annual review.',
  },
];

export default function DonationPage() {
  const [selectedType, setSelectedType] = useState<DonationFrequency>('one_time');
  const [amount, setAmount] = useState('50');
  const [projectLabel, setProjectLabel] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState<Donation | null>(null);
  const [priorTotal, setPriorTotal] = useState<number | null>(null);

  useEffect(() => {
    listMyDonations()
      .then((res) => setPriorTotal(res.total))
      .catch(() => setPriorTotal(null));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount <= 0) {
      setError('Enter an amount greater than 0.');
      return;
    }

    setSubmitting(true);
    try {
      const donation = await createDonation({
        amount: parsedAmount,
        frequency: selectedType,
        projectLabel: projectLabel || undefined,
        message: message || undefined,
      });
      setConfirmed(donation);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong recording your gift.');
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmed) {
    const newTotal = (priorTotal ?? 0) + Number(confirmed.amount);
    return (
      <div className="page heroPage">
        <div className="container">
          <PageHeader
            kicker="Thank You"
            title="Your gift has been recorded"
            subtitle="No payment was processed on this page — our team will follow up by email to complete your contribution."
          />

          <Card title="Gift summary">
            <p style={{ marginTop: 0, lineHeight: 1.7 }}>
              <strong>${Number(confirmed.amount).toFixed(2)}</strong>{' '}
              {GIVING_TYPES.find((g) => g.type === confirmed.frequency)?.title.toLowerCase()}
              {confirmed.project_label ? ` toward ${confirmed.project_label}` : ''}.
            </p>
            <p className="muted" style={{ marginTop: 8 }}>
              Your total recorded giving with SafariCo Hub so far: <strong>${newTotal.toFixed(2)}</strong>
            </p>
            <button
              type="button"
              className="btn btnPrimary"
              style={{ marginTop: 16 }}
              onClick={() => {
                setConfirmed(null);
                setPriorTotal(newTotal);
                setMessage('');
              }}
            >
              Make another gift
            </button>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="page heroPage">
      <div className="container">
        <PageHeader
          kicker="Donations"
          title="Fund visible community impact"
          subtitle="SafariCo Hub is a non-profit association — every gift goes directly to conservation, education, and community-led development across East Africa."
        />

        <p className="muted" style={{ marginTop: 0, lineHeight: 1.7, maxWidth: 900 }}>
          Choose the giving path that fits you, then confirm below. This records your gift with
          our team — we'll follow up by email to complete payment, no card details needed here.
        </p>

        {priorTotal !== null && priorTotal > 0 && (
          <p className="muted" style={{ marginTop: 0 }}>
            You've given <strong>${priorTotal.toFixed(2)}</strong> so far. Thank you.
          </p>
        )}

        <form onSubmit={handleSubmit}>
          <div className="grid cols2" style={{ gap: 18, marginTop: 24 }}>
            {GIVING_TYPES.map((g) => (
              <Card
                key={g.type}
                title={g.title}
                className={`selectable${selectedType === g.type ? ' selected' : ''}`}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedType(g.type)}
              >
                <p style={{ margin: 0, lineHeight: 1.6 }}>{g.description}</p>
              </Card>
            ))}
          </div>

          <div className="formSection" style={{ maxWidth: 420, margin: '28px auto 0' }}>
            <label>
              Amount (USD)
              <input
                type="number"
                min="1"
                step="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </label>

            {selectedType === 'sponsor_project' && (
              <label>
                Which project? (optional)
                <input
                  type="text"
                  value={projectLabel}
                  onChange={(e) => setProjectLabel(e.target.value)}
                  placeholder="e.g. Ranger patrol scholarships"
                />
              </label>
            )}

            <label>
              Message (optional)
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="A note for our team"
              />
            </label>

            <button
              type="submit"
              disabled={submitting}
              className="btn btnPrimary"
              style={{ width: '100%', marginTop: 16 }}
            >
              {submitting ? 'Recording your gift…' : `Confirm $${amount || '0'} gift`}
            </button>
            {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}
          </div>
        </form>
      </div>
    </div>
  );
}
